/**
 * Conditional Logger Utility
 * Only logs in development mode or when DEBUG is enabled
 * 
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.log('Message');      // Only in dev
 *   logger.info('Info');        // Only in dev
 *   logger.warn('Warning');     // Only in dev  
 *   logger.error('Error');      // Always logs (production errors)
 *   logger.debug('Debug');      // Only when DEBUG=true
 */

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
const isDebug = import.meta.env.VITE_DEBUG === 'true';

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LoggerOptions {
  prefix?: string;
  forceLog?: boolean;
}

class Logger {
  private prefix: string;
  private forceLog: boolean;

  constructor(options: LoggerOptions = {}) {
    this.prefix = options.prefix || '';
    this.forceLog = options.forceLog || false;
  }

  private shouldLog(level: LogLevel): boolean {
    // Errors always log
    if (level === 'error') return true;
    
    // Force log overrides environment
    if (this.forceLog) return true;
    
    // Debug only when VITE_DEBUG is set
    if (level === 'debug') return isDebug;
    
    // Everything else only in dev
    return isDev;
  }

  private formatMessage(message: string): string {
    return this.prefix ? `[${this.prefix}] ${message}` : message;
  }

  log(message: string, ...args: unknown[]): void {
    if (this.shouldLog('log')) {
      console.log(this.formatMessage(message), ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(message), ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(message), ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(this.formatMessage(message), ...args);
    }
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.debug(this.formatMessage(message), ...args);
    }
  }

  // Create a child logger with a prefix
  child(prefix: string): Logger {
    const newPrefix = this.prefix ? `${this.prefix}:${prefix}` : prefix;
    return new Logger({ prefix: newPrefix, forceLog: this.forceLog });
  }
}

// Default logger instance
export const logger = new Logger();

// Pre-configured loggers for common modules
export const authLogger = new Logger({ prefix: 'Auth' });
export const apiLogger = new Logger({ prefix: 'API' });
export const navigationLogger = new Logger({ prefix: 'Nav' });
export const themeLogger = new Logger({ prefix: 'Theme' });
export const stripeLogger = new Logger({ prefix: 'Stripe' });
export const supportLogger = new Logger({ prefix: 'Support' });
export const adminLogger = new Logger({ prefix: 'Admin' });
export const securityLogger = new Logger({ prefix: 'Security' });

// Factory function to create custom loggers
export function createLogger(prefix: string, options?: Omit<LoggerOptions, 'prefix'>): Logger {
  return new Logger({ ...options, prefix });
}

export default logger;











